import { create, show, useModal } from "@ebay/nice-modal-react";
import { useCallback } from "react";

import ConfirmationModal from "ds/components/ConfirmationModal";
import { ConfirmationModalMessage } from "ds/components/ConfirmationModal/Message";
import Box from "ds/components/Box";
import Banner from "ds/components/Banner";
import FlashContext from "components/FlashMessages/FlashContext";
import useTypedContext from "hooks/useTypedContext";
import useOpenTofuMigrate from "shared/Stack/useOpenTofuMigrate";
import { StackVendor } from "types/generated";

import { REFETCH_STACK_AND_STACK_SETTINGS_QUERIES } from "../../../constants";

const REFETCH_QUERIES = [
  ...REFETCH_STACK_AND_STACK_SETTINGS_QUERIES,
  "OpenTofuMigrationQueueSearch",
];

type StackSettingsOpenTofuRollbackConfirmationModalProps = {
  stackId: string;
};

const StackSettingsOpenTofuRollbackConfirmationModal = create(
  function StackSettingsOpenTofuRollbackConfirmationModal({
    stackId,
  }: StackSettingsOpenTofuRollbackConfirmationModalProps) {
    const modal = useModal();
    const { reportSuccess } = useTypedContext(FlashContext);

    const [migrate, { loading }] = useOpenTofuMigrate({
      refetchQueries: REFETCH_QUERIES,
    });

    const handleConfirm = useCallback(() => {
      migrate(
        {
          input: {
            first: 1,
            after: null,
            fullTextSearch: null,
            orderBy: null,
            requestedPage: null,
            predicates: [
              {
                field: "slug",
                exclude: null,
                constraint: {
                  booleanEquals: null,
                  enumEquals: null,
                  stringMatches: null,
                  stringEquals: [stackId],
                  hierarchyNodeValueEquals: null,
                  hierarchyNode: null,
                  timeInRange: null,
                  timeInLast: null,
                },
              },
            ],
          },
          target: StackVendor.Terraform,
          triggerRun: null,
        },
        (data) => {
          if (data?.openTofuMigrate) {
            reportSuccess({ message: "Stack vendor rollback was successfully queued" });
            modal.hide();
          }
        }
      );
    }, [migrate, modal, reportSuccess, stackId]);

    return (
      <ConfirmationModal
        title="Confirm Rollback"
        confirmationButtonLabel="Rollback"
        confirmationButtonVariant="dangerPrimary"
        onConfirm={handleConfirm}
        isLoading={loading}
        isDismissable={!loading}
      >
        <Box direction="column" gap="medium">
          <ConfirmationModalMessage>
            This will convert your OpenTofu stack back to a Terraform stack with the OpenTofu
            workflow tool.
          </ConfirmationModalMessage>
          <Banner variant="warning">
            The legacy Terraform vendor with OpenTofu workflow tool is deprecated and will be
            removed in the future.
          </Banner>
        </Box>
      </ConfirmationModal>
    );
  }
);

export const showStackSettingsOpenTofuRollbackConfirmationModal = (
  props: StackSettingsOpenTofuRollbackConfirmationModalProps
) => show(StackSettingsOpenTofuRollbackConfirmationModal, props);
